<script setup>

import { ref } from 'vue';
import { usePage } from '@inertiajs/inertia-vue3';
import AppLayout from "@/Layouts/AppLayout.vue";
import PrimaryButton from "@/Components/PrimaryButton.vue";
import SecondaryButton from "@/Components/SecondaryButton.vue";
import DialogModal from "@/Components/DialogModal.vue";
import InputLabel from "@/Components/InputLabel.vue";
import TextInput from "@/Components/TextInput.vue";
import { Inertia } from "@inertiajs/inertia";
import PaginationLinks from "@/Components/PaginationLinks.vue";
import { useHelpers, useTenantButtonVisibility } from "@/helpers";
import QuoteIndexFilters from "@/Components/Admin/Quote/QuoteIndexFilters.vue";

const props = defineProps({
  quotes: Object,
  companies: Array,
  filters: Object,
  clients: Array,
});
const deletingQuote = ref(null);
const duplicatingQuote = ref(null);
const helpers = useHelpers();
const { isButtonVisible } = useTenantButtonVisibility();
const currentUser = usePage().props.value.auth.user;

const formatDate = (timestamp, format) => {
  if (!timestamp) {
    return '';
  }
  return dayjs.tz(timestamp).format(format);
}

const duplicateQuote = (quote) => {
  duplicatingQuote.value = quote;
  Inertia.post(route('admin.quotes.duplicate', { quote: quote.qid }), {
    preserveScroll: true,
    onError: (error) => alert(`There was an error. Try again later`)
  });
  duplicatingQuote.value = null;
};

const deleteQuote = (quote) => {
  Inertia.delete(route('admin.quotes.destroy', { quote: quote.qid }), {
    preserveScroll: true,
    onError: (error) => alert(`There was an error. Try again later`)
  });
  deletingQuote.value = null;
};

const openDeleteQuoteModal = (quote) => {
  deletingQuote.value = quote;
}

const closeDeleteQuoteModal = () => {
  deletingQuote.value = null;
}
</script>

<template>
  <AppLayout title="Quote Request List">
    <template #header>
      <h2 class="font-semibold text-xl text-gray-800 leading-tight">
        Quote Request List
      </h2>
    </template>

    <div>
      <div class="mx-auto py-10 sm:px-6 lg:px-8">
        <div>
          <a :href="route('admin.quotes.create')" class="mr-2"
            v-if="helpers.userHasPermission(currentUser, 'generate_request') && isButtonVisible('quote.index.create')">
            <PrimaryButton>Add Quote Request</PrimaryButton>
          </a>
        </div>
        <div class="my-5 grid gap-2 lg:grid-cols-4 md:grid-cols-2 grid-cols-1">
          <QuoteIndexFilters :companies="companies" :clients="clients" :filters="filters" />
        </div>

        <div class="overflow-x-auto">
          <table class="min-w-full w-full text-left text-sm font-light">
            <thead class="border-b font-medium dark:border-neutral-500">
              <tr>
                <th scope="col" class="px-4 py-2">Client</th>
                <th scope="col" class="px-4 py-2">Company Name</th>
                <th scope="col" class="px-4 py-2">Project Name</th>
                <th scope="col" class="px-4 py-2">Quote Number</th>
                <th scope="col" class="px-4 py-2">Quote Date</th>
                <th scope="col" class="px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr class="border-b transition duration-300 ease-in-out hover:bg-neutral-200"
                v-for="quote in quotes.data">
                <td class="px-4 py-2 break-words" v-text="quote.client?.client"></td>
                <td class="px-4 py-2 break-words" v-text="quote.company?.company"></td>
                <td class="px-4 py-2 break-words" v-text="quote.project_name"></td>
                <td class="px-4 py-2 break-words" v-text="quote.po_number"></td>
                <td class="px-4 py-2 break-words" v-text="formatDate(quote.po_date, 'MM/DD/YYYY')"></td>
                <td>
                  <a :href="route('admin.quotes.show', { quote: quote })" class="fa fa-eye mr-1 p-1"
                    :title="`View ${quote.qid}`">
                  </a>
                  <a :href="route('admin.quotes.edit', { quote: quote })" class="fa fa-pen mr-1 p-1"
                    :title="`Edit quote ${quote.qid}`">
                  </a>
                  <button
                    v-if="isButtonVisible('quote.index.delete')"
                    class="fa fa-trash p-1"
                    @click="openDeleteQuoteModal(quote)"
                    :disabled="deletingQuote"
                    :title="`Delete quote ${quote.qid}`"></button>
                  <button
                    v-if="isButtonVisible('quote.index.duplicate')"
                    class="fa fa-clone p-1"
                    @click="duplicateQuote(quote)"
                    :disabled="duplicatingQuote"
                    :title="`Duplicate quote ${quote.qid}`"></button>
                </td>
              </tr>
            </tbody>
          </table>
          <div class="text-center mt-5">
            <PaginationLinks :pagination="quotes" />
          </div>
        </div>
      </div>
    </div>
    <DialogModal :show="deletingQuote !== null" @close="closeDeleteQuoteModal">
      <template #title>
        <b>Please confirm</b>
      </template>

      <template #content>
        You are about to delete quote <b v-text="deletingQuote.qid"></b>.
        <br>Are you sure?
      </template>

      <template #footer>
        <SecondaryButton @click="closeDeleteQuoteModal" class="mr-2">
          Cancel
        </SecondaryButton>
        <PrimaryButton @click="deleteQuote(deletingQuote)">
          Confirm
        </PrimaryButton>
      </template>
    </DialogModal>

  </AppLayout>
</template>
